(() => {
  const initialize = (root) => {
    if (root.dataset.homeReady) return;
    const tabs = [...root.querySelectorAll('[data-home-tab]')];
    const panels = [...root.querySelectorAll('[data-home-panel]')];
    if (!tabs.length || tabs.length !== panels.length) return;
    root.dataset.homeReady = 'true';
    root.querySelector('[data-home-tabs]').setAttribute('role', 'tablist');
    let pending;
    let controller;
    const contents = new Map();

    const fetchPanels = () => {
      if (pending) return pending;
      const url = new URL(window.location.href);
      url.hash = '';
      url.searchParams.delete('sections');
      url.searchParams.set('section_id', root.dataset.homeSection);
      controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 15000);
      pending = fetch(url.href, { credentials: 'same-origin', signal: controller.signal })
        .then(response => {
          if (!response.ok) throw new Error('Section response failed');
          return response.text();
        })
        .then(html => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const incoming = doc.getElementById(root.id);
          if (!incoming || incoming.dataset.homeRendering !== 'full') throw new Error('Incomplete section');
          panels.forEach(panel => {
            const source = doc.getElementById(panel.id);
            const list = source && incoming.contains(source) && source.querySelector('[data-home-products]');
            if (!list || source.hasAttribute('data-home-deferred')) throw new Error('Missing selection');
            list.querySelectorAll('script:not([type="application/json"])').forEach(script => script.remove());
            contents.set(panel.id, list);
          });
        })
        .catch(error => { pending = undefined; contents.clear(); throw error; })
        .finally(() => window.clearTimeout(timeout));
      return pending;
    };

    const loadPanel = async (panel) => {
      if (!panel.hasAttribute('data-home-deferred') || panel.hasAttribute('aria-busy')) return;
      const status = panel.querySelector('[data-home-status]');
      const retry = panel.querySelector('[data-home-retry]');
      panel.setAttribute('aria-busy', 'true');
      status.textContent = status.dataset.loadingLabel;
      status.hidden = false;
      retry.hidden = true;
      try {
        await fetchPanels();
        if (!root.isConnected) return;
        const list = contents.get(panel.id);
        if (!list) throw new Error('Missing selection');
        panel.querySelector('[data-home-products]').replaceWith(list.cloneNode(true));
        panel.removeAttribute('data-home-deferred');
        status.hidden = true;
        window.BBProductCardCarousel?.init(panel);
        window.BBProductCardOptions?.init(panel);
      } catch {
        if (root.isConnected) {
          status.textContent = status.dataset.errorLabel;
          retry.hidden = false;
        }
      } finally {
        panel.removeAttribute('aria-busy');
      }
    };

    const select = (selected, focus = false) => {
      tabs.forEach(tab => {
        const active = tab === selected;
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', tab.dataset.homeTab);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        tab.classList.toggle('is-active', active);
      });
      panels.forEach(panel => {
        panel.setAttribute('role', 'tabpanel');
        const hide = panel.id !== selected.dataset.homeTab;
        const wasVisible = !panel.hidden;
        panel.hidden = hide;
        if (hide && wasVisible) {
          panel.querySelectorAll('[data-bb-product-card]').forEach(card => {
            card.querySelector('[data-bb-options-modal][open] [data-bb-options-close]')?.click();
            card.dispatchEvent(new CustomEvent('bb:card-media-reset'));
          });
        }
        if (!hide) loadPanel(panel);
      });
      if (focus) selected.focus({ preventScroll: true });
    };
    root.addEventListener('click', event => {
      const retry = event.target.closest('[data-home-retry]');
      if (retry) { loadPanel(retry.closest('[data-home-panel]')); return; }
      const tab = event.target.closest('[data-home-tab]');
      if (!tab || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
      event.preventDefault();
      select(tab);
    });
    root.addEventListener('keydown', event => {
      const tab = event.target.closest('[data-home-tab]');
      if (!tab) return;
      const index = tabs.indexOf(tab);
      let next;
      if (event.key === 'ArrowRight') next = tabs[(index + 1) % tabs.length];
      if (event.key === 'ArrowLeft') next = tabs[(index - 1 + tabs.length) % tabs.length];
      if (event.key === 'Home') next = tabs[0];
      if (event.key === 'End') next = tabs[tabs.length - 1];
      if (event.key === ' ') next = tab;
      if (next) { event.preventDefault(); select(next, true); }
    });
    select(tabs.find(tab => tab.classList.contains('is-active')) || tabs[0]);
    root.addEventListener('shopify:block:select', event => {
      const panel = panels.find(item => item.dataset.homeBlock === event.detail.blockId);
      const tab = panel && tabs.find(item => item.dataset.homeTab === panel.id);
      if (tab) select(tab);
    });
    document.addEventListener('shopify:section:unload', event => {
      if (event.target.contains(root)) controller?.abort();
    });
  };
  const scan = (scope = document) => {
    if (scope.matches?.('[data-bb-home]')) initialize(scope);
    scope.querySelectorAll('[data-bb-home]').forEach(initialize);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  else scan();
  document.addEventListener('shopify:section:load', event => scan(event.target));
})();
